/**
 * UnconfirmedSendBanner — v0.17 send reservation (V220), review finding H1.
 *
 * When an invoice email's outcome could not be confirmed (the provider did not
 * answer, or the send has been running far longer than any send can), the
 * invoice stays locked so nothing changes under an email that may have gone
 * out. Without a way to settle it, the invoice was locked forever. This banner
 * tells the bookkeeper what happened in plain words and lets her settle it:
 * "It was sent" or "It wasn't sent" (the second asks her to check first,
 * because sending again could email the client twice).
 *
 * Data: GET /api/invoices/{id}/send-attempts/active. The query key sits under
 * ['invoices', id] so every existing invoice refresh also refreshes this.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { clearSendIntent, formatSentAt, sendErrorDetail } from '@/lib/invoiceSendIntent';

interface ActiveSendAttempt {
  attempt_id: string;
  state: 'PREPARING' | 'DISPATCHING' | 'UNKNOWN';
  started_at?: string | null;
  dispatch_started_at?: string | null;
  resolvable: boolean;
  resolvable_at?: string | null;
  to_recipients?: string[];
}

interface ActiveSendAttemptResponse {
  success?: boolean;
  attempt?: ActiveSendAttempt | null;
}

export interface UnconfirmedSendBannerProps {
  invoiceId: string;
  /** Shown in the "check with ..." warning. Falls back to "your client". */
  clientName?: string | null;
  /** Settling a send needs the same permission as sending. */
  canEdit: boolean;
}

export const activeSendAttemptQueryKey = (invoiceId: string) =>
  ['invoices', invoiceId, 'send-attempt-active'] as const;

export function UnconfirmedSendBanner({ invoiceId, clientName, canEdit }: UnconfirmedSendBannerProps) {
  const queryClient = useQueryClient();
  const [confirmNotSentOpen, setConfirmNotSentOpen] = useState(false);

  const { data } = useQuery({
    queryKey: activeSendAttemptQueryKey(invoiceId),
    queryFn: () => api.get(`/api/invoices/${invoiceId}/send-attempts/active`) as Promise<ActiveSendAttemptResponse>,
    enabled: canEdit && !!invoiceId,
    retry: false,
    // While a send holds the invoice, check back so the banner clears (or
    // becomes answerable) without a manual refresh.
    refetchInterval: (query) => (query.state.data?.attempt ? 30_000 : false),
  });
  const attempt = data?.attempt ?? null;

  const resolveMutation = useMutation({
    mutationFn: (outcome: 'sent' | 'not_sent') =>
      api.post(`/api/invoices/${invoiceId}/send-attempts/${attempt!.attempt_id}/resolve`, { outcome }) as Promise<{
        state: string; changed: boolean;
      }>,
    onSuccess: (result, outcome) => {
      // The browser's saved request key belonged to the send she just settled.
      // Left in place, the next Send would replay that old outcome instead of
      // emailing (review A1), so the next send starts with a new key.
      clearSendIntent(invoiceId);
      if (!result.changed) {
        toast.success(result.state === 'SENT'
          ? 'This email was already confirmed as sent.'
          : 'This email was already marked as not sent.');
      } else {
        toast.success(outcome === 'sent'
          ? 'Marked as sent. You can edit this invoice again.'
          : 'Marked as not sent. You can edit or send this invoice again.');
      }
      setConfirmNotSentOpen(false);
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['email-send-history', 'invoice', invoiceId] });
    },
    onError: (error: unknown) => {
      const detail = sendErrorDetail(error);
      toast.error((typeof detail === 'string' ? detail : detail?.message)
        || "That didn't save. Please try again.");
      setConfirmNotSentOpen(false);
      queryClient.invalidateQueries({ queryKey: activeSendAttemptQueryKey(invoiceId) });
    },
  });

  if (!canEdit || !attempt) return null;

  const startedAt = formatSentAt(attempt.dispatch_started_at || attempt.started_at);
  const who = clientName?.trim() || 'your client';
  const recipients = (attempt.to_recipients ?? []).join(', ');

  if (!attempt.resolvable) {
    return (
      <div
        className="mb-6 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
        data-testid="send-in-progress-banner"
        role="status"
      >
        This invoice's email is still being sent. Editing is paused until it finishes.
      </div>
    );
  }

  const pending = resolveMutation.isPending;
  return (
    <div
      className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      data-testid="unconfirmed-send-banner"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 space-y-2">
          <p className="font-medium">
            We couldn't confirm this invoice email was sent (started {startedAt}).
          </p>
          <p>
            {recipients ? <>It was going to {recipients}. </> : null}
            Until you confirm, this invoice can't be edited, cancelled or merged.
          </p>
          <p data-testid="unconfirmed-send-guidance">
            Not sure? Check your sent mail or ask the client before choosing.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {/* Equal weight on purpose: neither answer is the "right" one to click. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => resolveMutation.mutate('sent')}
              disabled={pending}
              data-testid="unconfirmed-send-was-sent"
            >
              {pending && resolveMutation.variables === 'sent' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              It was sent
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmNotSentOpen(true)}
              disabled={pending}
              data-testid="unconfirmed-send-not-sent"
            >
              It wasn't sent
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmNotSentOpen} onOpenChange={(open) => { if (!pending) setConfirmNotSentOpen(open); }}>
        <AlertDialogContent data-testid="unconfirmed-send-not-sent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this email as not sent?</AlertDialogTitle>
            <AlertDialogDescription>
              If you're not sure, check with {who} first. Sending again could email them twice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending} data-testid="unconfirmed-send-not-sent-cancel">
              Go back
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={(event) => {
                event.preventDefault();
                resolveMutation.mutate('not_sent');
              }}
              data-testid="unconfirmed-send-not-sent-confirm"
            >
              {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              It wasn't sent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
